<template>
  <MainLayout>
    <div class="parallax" data-parallax data-parallax-speed="0.25" data-parallax-direction="down">
      <div class="relative w-screen">
        <img
          class="absolute -left-[1vw] top-[-0vh] w-[20vw] opacity-80"
          src="/images/bg-visual-left-top.webp"
          alt="" />
        <img
          class="absolute -right-[6vw] top-[18vh] w-[30vw] opacity-60"
          src="/images/bg-visual-right-top.webp"
          alt="" />
      </div>
    </div>

    <section id="pricing" class="lg:!gap-12">
      <div class="flex items-center lg:mt-[2rem] lg:mb-[2rem]">
        <div class="flex flex-col gap-y-4.5 lg:gap-y-8">
          <h1
            class="text-[32px] leading-[36px] lg:text-[58px] lg:leading-[74px] font-bold text-dark text-center font-markPro tracking-[-0.1rem]">
            {{ t('pages.pricing.hero.heading.serve') }}
            <span class="text-gradient-primary whitespace-nowrap">{{
              t('pages.pricing.hero.heading.flexible')
            }}</span>
            {{ t('pages.pricing.hero.heading.scales') }}
          </h1>

          <div class="flex flex-col text-center leading-[27px] lg:text-lg font-semibold">
            <p class="text-lighter">{{ t('pages.pricing.hero.choose') }}</p>
            <p class="text-dark">{{ t('pages.pricing.hero.switch') }}</p>
          </div>

          <div class="flex justify-center">
            <div :class="{ 'opacity-40': toggleInt }" class="billing-toggle">
              <button
                type="button"
                :class="{ active: !yearly }"
                :aria-pressed="!yearly"
                :disabled="!!toggleInt"
                @click="yearly = false">
                {{ t('pages.pricing.hero.billing.monthly') }}
              </button>
              <button
                type="button"
                :class="{ active: yearly }"
                :aria-pressed="yearly"
                :disabled="!!toggleInt"
                @click="yearly = true">
                {{ t('pages.pricing.hero.billing.yearly') }}
                <span class="badge">
                  <span class="sm:hidden">{{ t('pages.pricing.hero.billing.badge_short') }}</span>
                  <span class="hidden sm:inline">{{ t('pages.pricing.hero.billing.badge') }}</span>
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="w-full grid grid-cols-1 lg:grid-cols-3 gap-y-8 lg:gap-x-8">
        <div class="col-span-1"></div>
        <div
          :class="{ switch: toggleInt }"
          class="toggle-wrapper col-span-2 flex gap-5 justify-center items-center mb-1">
          <span class="font-bold">{{ t('pages.pricing.content.toggle.subscription') }}</span>
          <div class="toggle" @click.stop="toggle()"><span></span></div>
          <span class="font-bold">{{ t('pages.pricing.content.toggle.volumefee') }}</span>
        </div>
        <div class="flex flex-col gap-8">
          <div
            class="flex flex-col gap-6 lg:gap-10 p-8 px-6 pb rounded-xl drop-shadow-md bg-gradient-primary-2">
            <h5
              class="font-markPro text-white text-center text-[24px] lg:text-[32px] font-bold tracking-[-0.03rem] lg:mb-6">
              {{ t('pages.pricing.content.build.title') }}
            </h5>

            <p class="font-semibold text-center text-white/70 px-2 max-lg:mb-2">
              <template v-if="toggleInt == 0"
                >{{ t('pages.pricing.content.build.text[0].the') }}
                <span class="text-white font-bold">{{
                  price(t('pages.pricing.content.build.text[0].first'))
                }}</span>
                {{ t('pages.pricing.content.build.text[0].month') }}
                <span class="text-white font-bold">{{
                  price(t('pages.pricing.content.build.text[0].additional'))
                }}</span>
                {{ t('pages.pricing.content.build.text[0].added') }}</template
              >
              <template v-else
                ><span class="text-white font-bold">{{
                  t('pages.pricing.content.build.text[1].no')
                }}</span>
                {{ t('pages.pricing.content.build.text[1].pay') }}
                <span class="text-white font-bold">{{
                  t('pages.pricing.content.build.text[1].all')
                }}</span></template
              >
            </p>

            <div
              :class="showYearly ? 'px-4' : 'px-6'"
              class="flex flex-col rounded-xl border border-white/50 bg-white/27 py-3.5">
              <div class="flex justify-between items-center text-white">
                <span class="font-bold text-xl">Base</span>
                <div
                  :class="{ 'gap-x-2': showYearly, 'gap-x-2.5': !toggleInt && !showYearly, 'gap-x-4': toggleInt }"
                  class="flex items-center">
                  <span
                    v-if="originalPrice(t(`pages.pricing.content.build.base.${toggleInt}`))"
                    class="original-price on-gradient"
                    >{{ t(`pages.pricing.content.build.base.${toggleInt}`) }}</span
                  >
                  <span
                    :class="showYearly ? 'text-[30px]' : 'text-[40px]'"
                    class="font-markPro font-bold tracking-[-0.1rem] whitespace-nowrap"
                    >{{ price(t(`pages.pricing.content.build.base.${toggleInt}`)) }}</span
                  >
                  <span class="text-white/70 text-[17px] pt-[6px] whitespace-nowrap"
                    >/ {{ t('pages.pricing.content.first.month') }}</span
                  >
                </div>
              </div>
              <div
                class="flex relative justify-between items-center text-white border-t border-white/30 mt-4.5 pt-4.5">
                <span
                  class="absolute -top-[35px] left-[56%] font-markPro font-bold tracking-[-0.1rem] text-[40px]"
                  >+</span
                >
                <span class="font-bold text-xl">Extra</span>
                <div :class="showYearly ? 'gap-x-2' : 'gap-x-4'" class="flex items-center">
                  <span
                    v-if="originalPrice(t(`pages.pricing.content.build.extra.${toggleInt}`))"
                    class="original-price on-gradient"
                    >{{ t(`pages.pricing.content.build.extra.${toggleInt}`) }}</span
                  >
                  <span class="font-markPro font-bold tracking-[-0.1rem] text-[38px] whitespace-nowrap">{{
                    price(t(`pages.pricing.content.build.extra.${toggleInt}`))
                  }}</span>
                  <span class="text-white/70 text-[17px] pt-[6px] whitespace-nowrap"
                    >/ {{ t('pages.pricing.content.first.month') }}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <div class="hidden sm:flex flex-col gap-6 p-6 bg-white rounded-xl drop-shadow-md">
            <h6
              class="font-markPro text-gradient-primary text-[32px] font-bold tracking-[-0.03rem]">
              {{ t('pages.pricing.content.contact.title') }}
            </h6>
            <p class="text-lighter font-bold max-w-70">
              {{ t('pages.pricing.content.contact.text') }}
            </p>
            <a
              href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1"
              target="_blank"
              class="btn btn-lg text-[15px] btn-primary text-center whitespace-nowrap"
              >{{ t('pages.pricing.content.contact.cta') }}</a
            >
          </div>
        </div>
        <div class="col-span-2 flex flex-col gap-6">
          <div
            class="hidden sm:flex justify-between p-6 py-7 pt-5 bg-white rounded-xl drop-shadow-md">
            <div>
              <h4
                class="font-markPro text-gradient-primary text-[32px] font-bold tracking-[-0.03rem]">
                {{ t(`pages.pricing.content.first.title.${toggleInt}`) }}
              </h4>
              <p class="text-lighter text-[15px]">(Website, Webshop, Kiosk or QR)</p>
            </div>
            <div class="flex items-center gap-x-4">
              <span
                v-if="originalPrice(t(`pages.pricing.content.first.price.${toggleInt}`))"
                class="original-price"
                >{{ originalPrice(t(`pages.pricing.content.first.price.${toggleInt}`)) }}</span
              >
              <h5 class="font-markPro font-bold tracking-[-0.1rem] text-[40px]">
                {{ price(t(`pages.pricing.content.first.price.${toggleInt}`)) }}
              </h5>
              <span class="text-lighter text-[17px] pt-[6px]"
                >/ {{ t('pages.pricing.content.first.month') }}</span
              >
            </div>
          </div>
          <div class="w-full grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div
              v-for="(plan, key, index) in tm('pages.pricing.plans')"
              :key="key"
              class="p-4 lg:px-6 lg:py-8 flex flex-col gap-y-8 justify-between h-full bg-white rounded-xl drop-shadow-md"
              :class="{ '!bg-[#F8F9FB] custom-dashed': index == 5 }">
              <div class="flex flex-col gap-y-0 lg:gap-y-1">
                <h4
                  class="font-markPro text-gradient-primary text-[32px] font-bold tracking-[-0.03rem]">
                  {{ plan.title }}
                </h4>

                <div class="flex items-center gap-x-4 min-h-[56px]">
                  <span v-if="originalPrice(plan.price[toggleInt])" class="original-price">{{
                    originalPrice(plan.price[toggleInt])
                  }}</span>
                  <h5
                    class="font-markPro font-bold tracking-[-0.1rem]"
                    :class="{ 'text-[30px]': index == 5, 'text-[40px]': index != 5 }">
                    {{ price(plan.price[toggleInt]) }}
                  </h5>
                  <span class="text-lighter text-[17px] pt-[6px]">{{
                    plan.period[toggleInt]
                  }}</span>
                </div>

                <p class="font-semibold pt-[0.5rem] pb-[1rem]">{{ plan.description }}</p>

                <ul>
                  <li
                    v-for="(feature, i) in plan.enabledFeatures"
                    :key="i"
                    class="flex items-center gap-x-2 text-lighter text-[15px] leading-[32px]">
                    <img src="/images/ico-check.svg" />
                    <span>{{ feature }}</span>
                  </li>

                  <li
                    v-for="(feature, i) in plan.disabledFeatures"
                    :key="i"
                    class="flex items-center gap-x-2 text-lighter">
                    <i class="bi bi-x text-lighter text-3xl"></i>
                    <span class="text-lighter">{{ feature }}</span>
                  </li>
                </ul>
              </div>

              <div class="flex flex-col gap-y-8 justify-center items-center">
                <a
                  v-if="[2, 3, 5].includes(index)"
                  href="https://webforms.pipedrive.com/f/6k1fJdz7xgA49xFsdiLKDuoJ6y0vj7QgYFKhNOo8Gf4SEA7lu5dJ0YDBJRJ67NCUn1"
                  target="_blank"
                  class="btn btn-lg text-[15px] btn-primary-outline text-center whitespace-nowrap !w-full"
                  >{{ plan.start }}
                </a>
                <a
                  v-else
                  v-track-event="'onboarding_start'"
                  href="https://merchant.bistroo.nl/register"
                  target="_blank"
                  class="btn btn-lg text-[15px] btn-primary-outline text-center whitespace-nowrap !w-full"
                  >{{ plan.start }}
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section id="cta" class="backgroundgradient backgroundgradient-bottom">
      <div class="w-full rounded-2xl py-16 max-md:px-14 lg:pt-26 lg:pb-20 relative overflow-hidden">
        <div class="absolute inset-0 z-10 bg-[#E3EEF7]"></div>
        <div
          class="parallax"
          data-parallax
          data-parallax-speed="0.05"
          data-parallax-direction="down">
          <div class="relative w-full h-full">
            <img
              class="absolute top-0 left-0 -translate-x-1/3 h-[150%] rotate-[45%]"
              src="/images/bg-visual-left-top.webp"
              alt="" />

            <img
              class="absolute right-0 translate-x-1/3 -top-1/4 lg:-top-1/2 h-1/2 lg:h-full rotate-[-20deg]"
              src="/images/bg-visual-right-top.webp"
              alt="" />
          </div>
        </div>

        <div class="flex flex-col gap-y-5.5 justify-center items-center text-center relative z-30">
          <h4
            class="text-[32px] leading-[42px] lg:text-5xl font-bold text-dark text-center font-markPro block tracking-[-0.08rem]">
            {{ t('main.cta_block.open_your_webshop_today') }}
            <span class="text-gradient-primary whitespace-nowrap">
              {{ t('main.cta_block.its_free') }}
            </span>
          </h4>

          <p class="text-lg lg:text-[24px] text-lighter font-semibold">
            {{ t('main.cta_block.start_generating_more_orders') }}
          </p>

          <a
            v-track-event="'onboarding_start'"
            href="https://merchant.bistroo.nl/register"
            class="btn btn-lg btn-primary flex items-center gap-x-3 mt-4">
            <span>
              {{ t('main.cta_block.get_started_for_free') }}
            </span>
            <i class="bi bi-arrow-right text-lg mt-[3px]"></i>
          </a>
        </div>
      </div>
    </section>
  </MainLayout>
</template>

<script setup lang="ts">
import MainLayout from '@/layouts/MainLayout.vue'
import { useHead } from '@unhead/vue'
import { computed, ref } from 'vue'
import { useI18n } from 'vue-i18n'

const { t, tm, locale } = useI18n()
useHead(computed(() => ({
  title: t('pages.pricing.seo.title'),
  meta: [{ name: 'description', content: t('pages.pricing.seo.description') }],
})))

const toggleInt = ref(0)

const toggle = () => {
  toggleInt.value = +!toggleInt.value
}

// Yearly billing applies to subscription prices only: 20% discount on the monthly price
const YEARLY_DISCOUNT = 0.8

const yearly = ref(true)
const showYearly = computed(() => yearly.value && !toggleInt.value)

const multiplyPrice = (value: string, factor: number) =>
  value.replace(/\d+(?:[.,]\d+)?/, (amount) => {
    const result = Math.round(parseFloat(amount.replace(',', '.')) * factor * 100) / 100

    return result.toLocaleString(locale.value, {
      minimumFractionDigits: Number.isInteger(result) ? 0 : 2,
      maximumFractionDigits: 2,
    })
  })

const price = (value: string) =>
  showYearly.value ? multiplyPrice(value, YEARLY_DISCOUNT) : value

// Monthly price without discount, shown struck through; skipped for free and custom prices
const originalPrice = (value: string) => (showYearly.value && /[1-9]/.test(value) ? value : null)
</script>

<style scoped>
@reference "@/assets/css/main.css";

.original-price {
  @apply font-markPro font-bold tracking-[-0.05rem] text-[24px] text-lighter/70 pt-[6px] line-through decoration-primary decoration-2;

  &.on-gradient {
    @apply text-[17px] text-white/60 decoration-white/80 whitespace-nowrap;
  }
}

.billing-toggle {
  @apply inline-flex items-center gap-x-1 p-1.5 bg-white rounded-full drop-shadow-md transition-opacity;

  button {
    @apply flex items-center gap-x-2.5 px-5 py-2.5 rounded-full font-bold text-dark cursor-pointer transition-colors;

    &.active {
      @apply bg-[#ff3696]/12 text-[#ff3696];
    }

    &:disabled {
      @apply cursor-default;
    }
  }

  .badge {
    @apply px-2.5 py-1 rounded-full text-white text-xs leading-none whitespace-nowrap;
    background: var(--color-gradient-primary);
  }
}

.toggle-wrapper {
  span {
    transition:
      transform 0.5s ease,
      color 0.5s ease;
  }

  span:last-of-type {
    @apply text-lighter/60;
  }

  .toggle {
    @apply p-[0.35rem] rounded-full w-13.5 cursor-pointer;
    background: var(--color-gradient-primary);

    span {
      @apply flex bg-white rounded-full w-4.5 h-4.5;
    }
  }

  &.switch {
    .toggle span {
      transform: translateX(1.55rem);
    }

    span:last-of-type {
      @apply text-dark;
    }
    span:first-of-type {
      @apply text-lighter/60;
    }
  }
}
</style>
